import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

// =============================================================================
// Schema & Types
// =============================================================================

const vehicleFormSchema = z.object({
  vehicleType: z.string().min(1, "Vehicle type is required"),
  registrationNumber: z.string().optional(),
  registrationState: z.string().optional(),
  vin: z.string().optional(),
  engineNumber: z.string().optional(),
  hullIdentificationNumber: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  variant: z.string().optional(),
  yearManufactured: z.string().optional(),
  bodyType: z.string().optional(),
  colour: z.string().optional(),
  odometerKm: z.string().optional(),
  engineSizeCc: z.string().optional(),
  transmission: z.string().optional(),
  fuelType: z.string().optional(),
  validationSource: z.string().optional(),
  validationDate: z.string().optional(),
  validationReference: z.string().optional(),
  lengthMetres: z.string().optional(),
  beamMetres: z.string().optional(),
});

type VehicleFormData = z.infer<typeof vehicleFormSchema>;

interface SecurityVehicle {
  id: string;
  securityId: string;
  vehicleType: string;
  registrationNumber: string | null;
  registrationState: string | null;
  vin: string | null;
  engineNumber: string | null;
  hullIdentificationNumber: string | null;
  make: string | null;
  model: string | null;
  variant: string | null;
  yearManufactured: number | null;
  bodyType: string | null;
  colour: string | null;
  odometerKm: number | null;
  engineSizeCc: number | null;
  transmission: string | null;
  fuelType: string | null;
  validationSource: string | null;
  validationDate: string | null;
  validationReference: string | null;
  lengthMetres: string | null;
  beamMetres: string | null;
}

export interface VehicleFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  existingVehicle?: SecurityVehicle | null;
}

// =============================================================================
// Constants
// =============================================================================

const VEHICLE_TYPE_OPTIONS = [
  { value: "motor_vehicle", label: "Motor Vehicle" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "caravan", label: "Caravan" },
  { value: "marine_boat", label: "Marine Boat" },
  { value: "marine_jetski", label: "Marine Jet Ski" },
  { value: "trailer", label: "Trailer" },
  { value: "other_vehicle", label: "Other Vehicle" },
];

const AUSTRALIAN_STATE_OPTIONS = [
  { value: "NSW", label: "NSW" },
  { value: "VIC", label: "VIC" },
  { value: "QLD", label: "QLD" },
  { value: "SA", label: "SA" },
  { value: "WA", label: "WA" },
  { value: "TAS", label: "TAS" },
  { value: "NT", label: "NT" },
  { value: "ACT", label: "ACT" },
];

const VALIDATION_SOURCE_OPTIONS = [
  { value: "redbook", label: "Redbook" },
  { value: "glass_guide", label: "Glass Guide" },
  { value: "manual_entry", label: "Manual Entry" },
  { value: "self_assessed", label: "Self Assessed" },
];

const MARINE_TYPES = ["marine_boat", "marine_jetski"];

function isMarineType(vehicleType: string): boolean {
  return MARINE_TYPES.includes(vehicleType);
}

const defaultValues: VehicleFormData = {
  vehicleType: "",
  registrationNumber: "",
  registrationState: "",
  vin: "",
  engineNumber: "",
  hullIdentificationNumber: "",
  make: "",
  model: "",
  variant: "",
  yearManufactured: "",
  bodyType: "",
  colour: "",
  odometerKm: "",
  engineSizeCc: "",
  transmission: "",
  fuelType: "",
  validationSource: "",
  validationDate: "",
  validationReference: "",
  lengthMetres: "",
  beamMetres: "",
};

// =============================================================================
// Component
// =============================================================================

export function VehicleFormModal({
  open,
  onOpenChange,
  securityId,
  existingVehicle,
}: VehicleFormModalProps) {
  const { toast } = useToast();
  const isEditing = !!existingVehicle;

  const form = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleFormSchema),
    defaultValues,
  });

  // Watch vehicleType to conditionally show marine fields
  const watchedVehicleType = form.watch("vehicleType");
  const showMarineFields = isMarineType(watchedVehicleType);

  // Reset form when modal opens or existingVehicle changes
  useEffect(() => {
    if (open) {
      if (existingVehicle) {
        form.reset({
          vehicleType: existingVehicle.vehicleType || "",
          registrationNumber: existingVehicle.registrationNumber || "",
          registrationState: existingVehicle.registrationState || "",
          vin: existingVehicle.vin || "",
          engineNumber: existingVehicle.engineNumber || "",
          hullIdentificationNumber: existingVehicle.hullIdentificationNumber || "",
          make: existingVehicle.make || "",
          model: existingVehicle.model || "",
          variant: existingVehicle.variant || "",
          yearManufactured: existingVehicle.yearManufactured?.toString() || "",
          bodyType: existingVehicle.bodyType || "",
          colour: existingVehicle.colour || "",
          odometerKm: existingVehicle.odometerKm?.toString() || "",
          engineSizeCc: existingVehicle.engineSizeCc?.toString() || "",
          transmission: existingVehicle.transmission || "",
          fuelType: existingVehicle.fuelType || "",
          validationSource: existingVehicle.validationSource || "",
          validationDate: existingVehicle.validationDate || "",
          validationReference: existingVehicle.validationReference || "",
          lengthMetres: existingVehicle.lengthMetres || "",
          beamMetres: existingVehicle.beamMetres || "",
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, existingVehicle, form]);

  const mutation = useMutation({
    mutationFn: async (data: VehicleFormData) => {
      const endpoint = `/api/crm/securities/${securityId}/vehicle`;
      const method = isEditing ? "PATCH" : "POST";

      // Convert numeric strings to numbers, strip empty strings to undefined
      const payload: Record<string, unknown> = {
        vehicleType: data.vehicleType,
        registrationNumber: data.registrationNumber || null,
        registrationState: data.registrationState || null,
        vin: data.vin || null,
        engineNumber: data.engineNumber || null,
        hullIdentificationNumber: data.hullIdentificationNumber || null,
        make: data.make || null,
        model: data.model || null,
        variant: data.variant || null,
        yearManufactured: data.yearManufactured ? parseInt(data.yearManufactured, 10) : null,
        bodyType: data.bodyType || null,
        colour: data.colour || null,
        odometerKm: data.odometerKm ? parseInt(data.odometerKm, 10) : null,
        engineSizeCc: data.engineSizeCc ? parseInt(data.engineSizeCc, 10) : null,
        transmission: data.transmission || null,
        fuelType: data.fuelType || null,
        validationSource: data.validationSource || null,
        validationDate: data.validationDate || null,
        validationReference: data.validationReference || null,
        lengthMetres: data.lengthMetres || null,
        beamMetres: data.beamMetres || null,
      };

      // For non-marine types, strip marine fields
      if (!isMarineType(data.vehicleType)) {
        payload.lengthMetres = null;
        payload.beamMetres = null;
        payload.hullIdentificationNumber = null;
      }

      const response = await apiRequest(method, endpoint, payload);
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `Failed to ${isEditing ? "update" : "create"} vehicle details`);
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate vehicle query to trigger re-fetch
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities", securityId, "vehicle"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities"],
      });
      toast({
        title: "Success",
        description: `Vehicle details ${isEditing ? "updated" : "saved"} successfully`,
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: VehicleFormData) => {
    mutation.mutate(data);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Vehicle Details" : "Add Vehicle Details"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the vehicle-specific details for this security."
              : "Add vehicle-specific details for this security."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Vehicle Type (required) */}
            <FormField
              control={form.control}
              name="vehicleType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vehicle Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-vehicle-type">
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {VEHICLE_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Identity Section */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="make"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Make</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Toyota"
                        data-testid="input-make"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. LandCruiser"
                        data-testid="input-model"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="variant"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Variant</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. GX"
                        data-testid="input-variant"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="yearManufactured"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year of Manufacture</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="e.g. 2022"
                        min={1900}
                        max={new Date().getFullYear() + 1}
                        data-testid="input-year-manufactured"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="colour"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Colour</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. White"
                        data-testid="input-colour"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bodyType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Body Type</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. SUV"
                        data-testid="input-body-type"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Registration Section */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="registrationNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registration Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. ABC123"
                        data-testid="input-registration-number"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="registrationState"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registration State</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === "none" ? "" : value)
                      }
                      value={field.value || "none"}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-registration-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Select state (optional)</SelectItem>
                        {AUSTRALIAN_STATE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>VIN</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Vehicle Identification Number"
                        data-testid="input-vin"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="engineNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Engine Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Engine number"
                        data-testid="input-engine-number"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Specifications Section */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="odometerKm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Odometer (km)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="e.g. 45000"
                        min={0}
                        data-testid="input-odometer-km"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="engineSizeCc"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Engine Size (cc)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="e.g. 2000"
                        min={0}
                        data-testid="input-engine-size-cc"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="transmission"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Transmission</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Automatic"
                        data-testid="input-transmission"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fuelType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fuel Type</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Petrol"
                        data-testid="input-fuel-type"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Marine-Specific Fields (conditional) */}
            {showMarineFields && (
              <div className="space-y-4">
                <div className="text-sm font-medium text-muted-foreground border-t pt-3">
                  Marine-Specific Details
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="hullIdentificationNumber"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Hull Identification Number (HIN)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Hull identification number"
                            data-testid="input-hull-identification-number"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lengthMetres"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Length (metres)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="e.g. 6.50"
                            min={0}
                            data-testid="input-length-metres"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="beamMetres"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Beam (metres)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="e.g. 2.40"
                            min={0}
                            data-testid="input-beam-metres"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Validation Section */}
            <div className="space-y-4">
              <div className="text-sm font-medium text-muted-foreground border-t pt-3">
                Validation
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="validationSource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Validation Source</FormLabel>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(value === "none" ? "" : value)
                        }
                        value={field.value || "none"}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-validation-source">
                            <SelectValue placeholder="Select source" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Select source (optional)</SelectItem>
                          {VALIDATION_SOURCE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="validationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Validation Date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          data-testid="input-validation-date"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="validationReference"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Validation Reference</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Reference number or URL"
                          data-testid="input-validation-reference"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={mutation.isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mutation.isPending}
                data-testid="button-save"
              >
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? "Save" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
